import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { VacancyDetail } from '@/types/recruitment';
import {
  usePipelineTemplates,
  useUpdateVacancy,
  useAddPipelineStage,
  useUpdatePipelineStage,
  useDeletePipelineStage,
  useReorderPipelineStages,
  useVacancyPipeline,
} from '@/hooks/use-recruitment';
import {
  PipelineStagesEditor,
  type AddStageInput,
  type UpdateStageInput,
} from './pipeline-stages-editor';

interface VacancyPipelineTabProps {
  vacancy: VacancyDetail;
}

export function VacancyPipelineTab({ vacancy }: VacancyPipelineTabProps) {
  const stages = vacancy.pipelineStages;

  const { data: templates } = usePipelineTemplates();
  const { data: pipelineData } = useVacancyPipeline(vacancy.id);
  const countByStageId = new Map(
    pipelineData?.stages.map((s) => [s.id, s.count]) ?? [],
  );
  const updateVacancy = useUpdateVacancy();
  const addMutation = useAddPipelineStage(vacancy.id);
  const updateMutation = useUpdatePipelineStage(vacancy.id);
  const deleteMutation = useDeletePipelineStage(vacancy.id);
  const reorderMutation = useReorderPipelineStages(vacancy.id);

  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);

  const handleTemplatePick = (templateId: string) => {
    if (stages.length > 0) {
      setPendingTemplateId(templateId);
    } else {
      applyTemplate(templateId);
    }
  };

  const applyTemplate = (templateId: string) => {
    updateVacancy.mutate({ id: vacancy.id, pipelineTemplateId: templateId });
    setPendingTemplateId(null);
  };

  const handleAdd = (input: AddStageInput) => {
    addMutation.mutate(input);
  };

  const handleUpdate = (stageId: string, input: UpdateStageInput) => {
    updateMutation.mutate({ stageId, ...input });
  };

  const handleDelete = (stageId: string) => {
    deleteMutation.mutate(stageId);
  };

  const handleReorder = (positions: { id: string; position: number }[]) => {
    reorderMutation.mutate({ stages: positions });
  };

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <label className="text-[13px] font-semibold text-foreground">Hiring Cycle</label>
        <p className="text-xs text-muted-foreground">
          Start from a template, then customize stages below.
        </p>
        {templates && templates.length === 0 ? (
          <div className="flex h-9 w-[300px] items-center rounded-md border border-dashed border-border px-3 text-xs text-muted-foreground">
            No templates available yet
          </div>
        ) : (
          <Select
            onValueChange={handleTemplatePick}
            disabled={!templates || templates.length === 0}
          >
            <SelectTrigger className="h-9 w-[300px]">
              <SelectValue placeholder="Change pipeline template..." />
            </SelectTrigger>
            <SelectContent>
              {templates?.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                  {t.isDefault && ' (Default)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <PipelineStagesEditor
        stages={stages}
        countByStageId={countByStageId}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onReorder={handleReorder}
        pending={{ add: addMutation.isPending, update: updateMutation.isPending }}
      />

      <AlertDialog
        open={!!pendingTemplateId}
        onOpenChange={(v) => {
          if (!v) setPendingTemplateId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Pipeline?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace the current stages. Any custom stages will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer"
              onClick={() => pendingTemplateId && applyTemplate(pendingTemplateId)}
            >
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
